import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  FormHelperText,
  TextField,
} from "@mui/material";
import { Commodity } from "@/pages/sy/models/Commodity";
import { Autocomplete } from "@mui/material";

interface RecipeLayerModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (
    commodityId: number | null,
    weight: number,
    chargeNumber: number,
  ) => void;
  commodities: Commodity[];
  isLoading: boolean;
  isSaving?: boolean;
  title: string;
  recipeId: number | null;
  chargeNumber?: number;
}

export const RecipeLayerModal: React.FC<RecipeLayerModalProps> = ({
  open,
  onClose,
  onSave,
  commodities,
  isLoading,
  isSaving = false,
  title,
  recipeId,
  chargeNumber = 1,
}) => {
  const [commodityId, setCommodityId] = useState<number | null>(null);
  const [weight, setWeight] = useState<number>(0);
  const [weightInput, setWeightInput] = useState<string>("");
  const [showError, setShowError] = useState<boolean>(false);

  const enabledCommodities = commodities.filter((c) => c.IsEnable);

  useEffect(() => {
    if (open) {
      setCommodityId(null);
      setWeight(0);
      setWeightInput("");
      setShowError(false);
    }
  }, [open]);

  const handleSave = () => {
    if (!commodityId) {
      setShowError(true);
      return;
    }

    onSave(commodityId, weight, chargeNumber);
    // Modal closing is handled by parent via onSuccess callback
  };

  const handleCommodityChange = (id: number | null) => {
    setCommodityId(id);
    if (id) setShowError(false);
  };

  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setWeightInput(newValue);

    if (newValue === "") {
    } else {
      const value = parseFloat(newValue);
      if (!isNaN(value)) {
        setWeight(value);
      }
    }
  };

  const handleWeightBlur = () => {
    if (weightInput === "") {
      setWeightInput("0");
      setWeight(0);
    }
  };

  const selectedCommodity = commodityId
    ? enabledCommodities.find((c) => c.CommodityId === commodityId)
    : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: "30%",
            maxWidth: "700px",
            borderRadius: "12px",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: (theme) => theme.palette.primary.main,
          color: "white",
          padding: (theme) => theme.spacing(2),
          "& .MuiTypography-root": {
            fontSize: "1.5rem",
            fontWeight: 600,
          },
        }}
      >
        {title}
      </DialogTitle>

      <DialogContent
        sx={{
          padding: (theme) => theme.spacing(3),
          "&.MuiDialogContent-root": {
            paddingTop: (theme) => theme.spacing(3),
          },
          display: "flex",
          flexDirection: "column",
          gap: (theme) => theme.spacing(2),
        }}
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <>
            <Box>
              <Autocomplete
                options={enabledCommodities}
                getOptionLabel={(option) => {
                  if (typeof option === "object" && option !== null) {
                    return option.CommodityName || "";
                  }
                  return "";
                }}
                value={selectedCommodity}
                onChange={(_, newValue) => {
                  handleCommodityChange(newValue ? newValue.CommodityId : null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Commodity"
                    variant="outlined"
                    size="small"
                    fullWidth
                    required
                    error={showError}
                  />
                )}
              />
              {showError && (
                <FormHelperText
                  sx={{
                    color: (theme) => theme.palette.error.main,
                    marginTop: (theme) => theme.spacing(1),
                  }}
                >
                  Commodity selection is required
                </FormHelperText>
              )}
            </Box>

            <TextField
              label="Weight"
              variant="outlined"
              value={weightInput}
              onChange={handleWeightChange}
              onBlur={handleWeightBlur}
              size="small"
              fullWidth
              type="number"
              slotProps={{
                htmlInput: {
                  min: 0,
                },
              }}
            />
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          padding: (theme) => theme.spacing(2, 3, 3),
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: (theme) => theme.spacing(2),
            marginLeft: "auto",
          }}
        >
          <Button
            onClick={onClose}
            disabled={isLoading || isSaving}
            variant="contained"
            sx={{
              padding: (theme) => theme.spacing(1, 3),
              minWidth: "120px",
              borderRadius: "4px",
              backgroundColor: "#9e9e9e",
              color: "white",
              "&:hover": {
                backgroundColor: "#757575",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSave}
            disabled={isLoading || !commodityId || isSaving}
            variant="contained"
            sx={{
              padding: (theme) => theme.spacing(1, 3),
              minWidth: "120px",
              borderRadius: "4px",
              backgroundColor: (theme) => theme.palette.primary.main,
              color: "white",
              "&:hover": {
                backgroundColor: (theme) => theme.palette.primary.dark,
              },
            }}
          >
            {isSaving ? <CircularProgress size={24} color="inherit" /> : "Save"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
